import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { JService } from "../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import {
  checkboxCheckedAll,
  checkboxUncheck,
  chevronDown,
  filterLines,
  modalCloseIcon,
} from "../../utility-functions/assetLoader";
import { Checkbox } from "@mui/material";
import GTable from "../design/components/GTable";
import "./HubspotContactsTable.css";
import MultiSelectDropdown from "../design/components/MultiSelectDropdown";
import { useSelector } from "react-redux";
import ContainsTokenDropdown from "../design/components/ContainsTokenDropdown";
import DateFilterDropdown from "../design/components/DateFilterDropdown";
import { filtersReducer } from "../../utility-functions";

const filterOptions = [
  { id: "email", value: "email" },
  { id: "lifecyclestage", value: "lifecyclestage" },
  { id: "lastmodifieddate", value: "lastmodifieddate" },
  { id: "createdate", value: "createdate" },
  { id: "hubspot_owner_id", value: "hubspot_owner" },
  { id: "hs_analytics_source", value: "hs_analytics_source" },
  { id: "country", value: "country" },
  { id: "notes_last_contacted", value: "notes_last_contacted" },
  { id: "hs_lead_status", value: "hs_lead_status" },
  { id: "jobtitle", value: "jobtitle" },
];

const lifeCycleStageOptions = [
  { id: "subscriber", value: "subscriber" },
  { id: "lead", value: "lead" },
  { id: "marketingqualifiedlead", value: "marketingqualifiedlead" },
  { id: "salesqualifiedlead", value: "salesqualifiedlead" },
  { id: "opportunity", value: "opportunity" },
  { id: "customer", value: "customer" },
  { id: "evangelist", value: "evangelist" },
  { id: "other", value: "other" },
];

const hsAnalyticsSourceOptions = [
  { id: "ORGANIC_SEARCH", value: "ORGANIC_SEARCH" },
  { id: "PAID_SEARCH", value: "PAID_SEARCH" },
  { id: "EMAIL_MARKETING", value: "EMAIL_MARKETING" },
  { id: "SOCIAL_MEDIA", value: "SOCIAL_MEDIA" },
  { id: "DIRECT_TRAFFIC", value: "DIRECT_TRAFFIC" },
  { id: "REFERRALS", value: "REFERRALS" },
  { id: "PAID_SOCIAL", value: "PAID_SOCIAL" },
  { id: "ORGANIC_SOCIAL", value: "ORGANIC_SOCIAL" },
  { id: "OTHER_CAMPAIGNS", value: "OTHER_CAMPAIGNS" },
  { id: "OFFLINE", value: "OFFLINE" },
];

const hsLeadStatusOptions = [
  { id: "NEW", value: "NEW" },
  { id: "OPEN", value: "OPEN" },
  { id: "IN_PROGRESS", value: "IN_PROGRESS" },
  { id: "OPEN_DEAL", value: "OPEN_DEAL" },
  { id: "UNQUALIFIED", value: "UNQUALIFIED" },
  { id: "ATTEMPTED_TO_CONTACT", value: "ATTEMPTED_TO_CONTACT" },
  { id: "CONNECTED", value: "CONNECTED" },
  { id: "BAD_TIMING", value: "BAD_TIMING" },
];

const initialFilterDropdownState = filterOptions.reduce(
  (acc, filter) => {
    acc[filter.id] = false;
    return acc;
  },
  { allFilters: false }
);

const initialState = {
  searchText: "",
  searchFields: [],
};

const columns = [
  {
    field: "name",
    headerName: "Name",
    flex: 0.5,
    renderCell: (params) => <span>{params.row.name}</span>,
  },
  {
    field: "phone",
    headerName: "Phone number",
    flex: 0.5,
    renderCell: (params) => <span>{params.row.phone}</span>,
  },
];

function useDropdownPosition(isOpen, dropdownWidth = 320, containerRef) {
  const buttonRef = useRef(null);
  const [style, setStyle] = useState({
    left: 0,
  });

  useEffect(() => {
    if (!isOpen || !buttonRef.current || !containerRef.current) return;

    const btnRect = buttonRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const spaceOnRight = containerRect.right - btnRect.left;

    if (spaceOnRight >= dropdownWidth) {
      setStyle({ left: 0 });
    } else {
      setStyle({ right: 0 });
    }
  }, [isOpen, dropdownWidth, containerRef]);

  return [buttonRef, style];
}

const HubspotContactsTable = ({ setSelectedHubspotContacts }) => {
  const locations = useSelector((state) => state?.locations?.value);
  const filterContainerRef = useRef(null);

  //States
  const [hubspotSelectionModel, setHubspotSelectionModel] = useState([]);
  const [hubspotContacts, setHubspotContacts] = useState([]);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(
    initialFilterDropdownState
  );
  const [selectedFilters, setSelectedFilters] = useState({
    allFilters: [],
    lifecyclestage: [],
    hubspot_owner_id: [],
    hs_analytics_source: [],
    country: [],
    hs_lead_status: [],
  });
  const [hubspotOwners, setHubspotOwners] = useState([]);
  const [countries, setCountries] = useState([]);
  const [emailFilterVal, setEmailFilterVal] = useState("");
  const [jobtitleFilterVal, setJobtitleFilterVal] = useState("");
  const [dates, setDates] = useState({
    lastmodifieddate: { startDate: null, endDate: null },
    createdate: { startDate: null, endDate: null },
    notes_last_contacted: { startDate: null, endDate: null },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState
  );
  const [filteredContacts, setFilteredContacts] = useState([]);

  const rows = useMemo(
    () =>
      filteredContacts?.map((c) => {
        const first = c?.properties?.firstname || "";
        const last = c?.properties?.lastname || "";
        const fullName = `${first} ${last}`.trim();

        return {
          id: c?.id,
          name: fullName || "-",
          phone: c?.properties?.phone || "-",
        };
      }),
    [filteredContacts]
  );

  const allRowsSelected = useMemo(
    () =>
      filteredContacts?.length > 0 &&
      filteredContacts.every((c) => hubspotSelectionModel.includes(c.id)),
    [filteredContacts, hubspotSelectionModel]
  );

  //Handlers
  const toggleFilterDropdown = (id) => {
    setShowFiltersDropdown((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const addFilterHandler = (id, selected) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [id]: selected,
    }));
    setShowFiltersDropdown(initialFilterDropdownState);
  };

  const clearFilterHandler = (id) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [id]: [],
    }));
    setShowFiltersDropdown(initialFilterDropdownState);
  };

  const handleHubspotSelectionChange = (newSelection) => {
    const newIds = newSelection.map((obj) => obj.id);
    setHubspotSelectionModel(newIds);
    setSelectedHubspotContacts(
      filteredContacts.filter((c) => newIds.includes(c.id))
    );
  };

  const handleHubspotHeaderCheckboxChange = (e) => {
    if (e.target.checked) {
      setHubspotSelectionModel(filteredContacts.map((c) => c.id));
      setSelectedHubspotContacts(filteredContacts);
    } else {
      setHubspotSelectionModel([]);
      setSelectedHubspotContacts([]);
    }
  };

  const buildFiltersPayload = () => {
    const filters = [];

    selectedFilters.allFilters.forEach((filterId) => {
      switch (filterId) {
        case "email":
          if (emailFilterVal) {
            filters.push({
              propertyName: "email",
              operator: "CONTAINS_TOKEN",
              value: emailFilterVal,
            });
          }
          break;

        case "jobtitle":
          if (jobtitleFilterVal) {
            filters.push({
              propertyName: "jobtitle",
              operator: "CONTAINS_TOKEN",
              value: jobtitleFilterVal,
            });
          }
          break;

        case "lifecyclestage":
        case "hubspot_owner_id":
        case "hs_analytics_source":
        case "country":
        case "hs_lead_status":
          if (selectedFilters[filterId]?.length > 0) {
            filters.push({
              propertyName: filterId,
              operator: "IN",
              values: selectedFilters[filterId],
            });
          }
          break;

        case "lastmodifieddate":
        case "createdate":
        case "notes_last_contacted":
          const { startDate, endDate } = dates[filterId] || {};
          if (startDate && endDate) {
            filters.push({
              propertyName: filterId,
              operator: "BETWEEN",
              start: startDate,
              end: endDate,
            });
          }
          break;

        default:
          break;
      }
    });

    return { filters };
  };

  const getHubspotContacts = () => {
    setIsLoading(true);
    const payload = buildFiltersPayload();

    JService.post(`/api/v2/integrations/hubspot-filters/`, payload)
      .then((response) => {
        if (!response?.success)
          throw new Error(
            response?.message || "Error while fetching hubspot contacts"
          );
        setHubspotContacts(response?.total_result);
        setFilteredContacts(response?.total_result);
      })
      .catch((err) =>
        enqueueSnackbar(
          err?.message || "Error while fetching hubspot contacts",
          {
            variant: "error",
          }
        )
      )
      .finally(() => setIsLoading(false));
  };

  const getHubspotOwners = () => {
    JService.get("/api/v2/integrations/hubspot-owners/")
      .then((response) => {
        if (!response?.success)
          throw new Error(
            response?.message || "Error while fetching hubspot owners"
          );
        setHubspotOwners(
          response?.owners?.map((o) => {
            return {
              id: o.id,
              value: (o.firstName + " " + o.lastName).trim() ?? "",
            };
          }) || []
        );
      })
      .catch((err) =>
        enqueueSnackbar(err?.message || "Error while fetching hubspot owners", {
          variant: "error",
        })
      );
  };

  const onSearch = (searchText) => {
    const searchTextLower = searchText.toLowerCase();

    const temp = hubspotContacts?.filter((c) => {
      const first = c?.properties?.firstname?.toLowerCase() || "";
      const last = c?.properties?.lastname?.toLowerCase() || "";
      const phone = String(c?.properties?.phone || "").toLowerCase();

      const fullName = `${first} ${last}`.trim();

      return (
        first.includes(searchTextLower) ||
        last.includes(searchTextLower) ||
        fullName.includes(searchTextLower) ||
        phone.includes(searchTextLower)
      );
    });

    setFilteredContacts(temp);
  };

  //Effects
  useEffect(() => {
    onSearch(availableData?.searchText);
  }, [availableData]);

  useEffect(() => {
    getHubspotContacts();
  }, [selectedFilters, emailFilterVal, jobtitleFilterVal, dates]);

  useEffect(() => {
    setHubspotSelectionModel(filteredContacts?.map((c) => c.id));
    setSelectedHubspotContacts(filteredContacts);
  }, [filteredContacts]);

  useEffect(() => {
    if (selectedFilters?.allFilters?.includes("hubspot_owner_id")) {
      getHubspotOwners();
    }
    if (selectedFilters?.allFilters?.includes("country") && locations) {
      setCountries(
        locations?.map((l) => {
          return { id: l.id, value: l.country };
        })
      );
    }
  }, [selectedFilters?.allFilters, locations]);

  return (
    <GTable
      leftHeader={{
        isSearch: true,
        filters: [],
      }}
      dispatchAvailableData={dispatchAvailableData}
      availableData={availableData}
      isLoading={isLoading}
      rightHeader={{
        filtersDropdown: (
          <div className="hubspot-filter-container">
            <button
              className="hubspot-filter-button"
              onClick={() => toggleFilterDropdown("allFilters")}
            >
              {filterLines("20", "20")}
              <span>Filter</span>
            </button>
            {showFiltersDropdown?.allFilters && (
              <MultiSelectDropdown
                style={{
                  position: "absolute",
                  right: "0",
                  top: "42px",
                  zIndex: "9999",
                }}
                options={filterOptions}
                onAdd={(selected) => addFilterHandler("allFilters", selected)}
                onClear={() => clearFilterHandler("allFilters")}
                secondButtonName="Add"
                maxSelected={5}
                selected={selectedFilters?.allFilters}
              />
            )}
          </div>
        ),
      }}
      extraFilters={
        selectedFilters?.allFilters?.length > 0 ? (
          <div ref={filterContainerRef} className="applied-filters-container">
            {selectedFilters?.allFilters?.includes("lifecyclestage") && (
              <AppliedFilterChip
                id="lifecyclestage"
                label="lifecyclestage"
                selected={selectedFilters.lifecyclestage}
                showDropdown={showFiltersDropdown.lifecyclestage}
                options={lifeCycleStageOptions}
                onOpenDropdown={() => toggleFilterDropdown("lifecyclestage")}
                onClear={() => clearFilterHandler("lifecyclestage")}
                onAdd={(selected) =>
                  addFilterHandler("lifecyclestage", selected)
                }
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("hs_analytics_source") && (
              <AppliedFilterChip
                id="hs_analytics_source"
                label="hs_analytics_source"
                selected={selectedFilters.hs_analytics_source}
                showDropdown={showFiltersDropdown.hs_analytics_source}
                options={hsAnalyticsSourceOptions}
                onOpenDropdown={() =>
                  toggleFilterDropdown("hs_analytics_source")
                }
                onClear={() => clearFilterHandler("hs_analytics_source")}
                onAdd={(selected) =>
                  addFilterHandler("hs_analytics_source", selected)
                }
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("hs_lead_status") && (
              <AppliedFilterChip
                id="hs_lead_status"
                label="hs_lead_status"
                selected={selectedFilters.hs_lead_status}
                showDropdown={showFiltersDropdown.hs_lead_status}
                options={hsLeadStatusOptions}
                onOpenDropdown={() => toggleFilterDropdown("hs_lead_status")}
                onClear={() => clearFilterHandler("hs_lead_status")}
                onAdd={(selected) =>
                  addFilterHandler("hs_lead_status", selected)
                }
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("hubspot_owner_id") && (
              <AppliedFilterChip
                id="hubspot_owner_id"
                label="hubspot_owner"
                selected={selectedFilters.hubspot_owner_id}
                showDropdown={showFiltersDropdown.hubspot_owner_id}
                options={hubspotOwners}
                onOpenDropdown={() => toggleFilterDropdown("hubspot_owner_id")}
                onClear={() => clearFilterHandler("hubspot_owner_id")}
                onAdd={(selected) =>
                  addFilterHandler("hubspot_owner_id", selected)
                }
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("country") && (
              <AppliedFilterChip
                id="country"
                label="country"
                selected={selectedFilters.country}
                showDropdown={showFiltersDropdown.country}
                options={countries}
                onOpenDropdown={() => toggleFilterDropdown("country")}
                onClear={() => clearFilterHandler("country")}
                onAdd={(selected) => addFilterHandler("country", selected)}
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("email") && (
              <AppliedFilterChip
                id="email"
                label="email"
                showDropdown={showFiltersDropdown.email}
                onOpenDropdown={() => toggleFilterDropdown("email")}
                onClear={() => toggleFilterDropdown("email")}
                type="token"
                initialValue={emailFilterVal}
                onApply={(v) => setEmailFilterVal(v)}
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("jobtitle") && (
              <AppliedFilterChip
                id="jobtitle"
                label="jobtitle"
                showDropdown={showFiltersDropdown.jobtitle}
                onOpenDropdown={() => toggleFilterDropdown("jobtitle")}
                onClear={() => toggleFilterDropdown("jobtitle")}
                type="token"
                initialValue={jobtitleFilterVal}
                onApply={(v) => setJobtitleFilterVal(v)}
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("lastmodifieddate") && (
              <AppliedFilterChip
                id="lastmodifieddate"
                label="lastmodifieddate"
                showDropdown={showFiltersDropdown.lastmodifieddate}
                onOpenDropdown={() => toggleFilterDropdown("lastmodifieddate")}
                onClear={() =>
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    lastmodifieddate: false,
                  }))
                }
                type="date"
                onApply={(sd, ed) => {
                  setDates((prev) => ({
                    ...prev,
                    lastmodifieddate: { startDate: sd, endDate: ed },
                  }));
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    lastmodifieddate: false,
                  }));
                }}
                initialStartDate={dates.lastmodifieddate.startDate}
                initialEndDate={dates.lastmodifieddate.endDate}
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("createdate") && (
              <AppliedFilterChip
                id="createdate"
                label="createdate"
                showDropdown={showFiltersDropdown.createdate}
                onOpenDropdown={() => toggleFilterDropdown("createdate")}
                onClear={() =>
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    createdate: false,
                  }))
                }
                type="date"
                onApply={(sd, ed) => {
                  setDates((prev) => ({
                    ...prev,
                    createdate: { startDate: sd, endDate: ed },
                  }));
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    createdate: false,
                  }));
                }}
                initialStartDate={dates.createdate.startDate}
                initialEndDate={dates.createdate.endDate}
                containerRef={filterContainerRef}
              />
            )}

            {selectedFilters?.allFilters?.includes("notes_last_contacted") && (
              <AppliedFilterChip
                id="notes_last_contacted"
                label="notes_last_contacted"
                showDropdown={showFiltersDropdown.notes_last_contacted}
                onOpenDropdown={() =>
                  toggleFilterDropdown("notes_last_contacted")
                }
                onClear={() =>
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    notes_last_contacted: false,
                  }))
                }
                type="date"
                onApply={(sd, ed) => {
                  setDates((prev) => ({
                    ...prev,
                    notes_last_contacted: { startDate: sd, endDate: ed },
                  }));
                  setShowFiltersDropdown((prev) => ({
                    ...prev,
                    notes_last_contacted: false,
                  }));
                }}
                initialStartDate={dates.notes_last_contacted.startDate}
                initialEndDate={dates.notes_last_contacted.endDate}
                containerRef={filterContainerRef}
              />
            )}
          </div>
        ) : null
      }
      columns={columns}
      rows={rows}
      rowsPerPage={filteredContacts?.length || 100}
      checkboxSelection={true}
      checkboxVisibility={true}
      headerCheckbox={
        <Checkbox
          sx={{
            zIndex: 100,
            "&.Mui-checked": {
              color: "2EB258",
            },
          }}
          checkedIcon={checkboxCheckedAll()}
          icon={checkboxUncheck()}
          checked={allRowsSelected}
          onChange={handleHubspotHeaderCheckboxChange}
        />
      }
      headerCheckboxSelection={false}
      selectionModel={hubspotSelectionModel}
      onSelectionModelChange={handleHubspotSelectionChange}
      useMuiTable={true}
      hidePagination={true}
      rowCount={filteredContacts?.length}
      fromScreen="hubspot-contacts"
      onRowClick={() => null}
      className="hubspot-contacts-gtable"
      styleOverride={{ marginBottom: 0 }}
      onSearch={onSearch}
    />
  );
};

export default HubspotContactsTable;

export const AppliedFilterChip = ({
  id,
  label,
  selected,
  showDropdown,
  options,
  onOpenDropdown,
  onClear,
  onAdd,
  containerRef,
  type = "multi", // "multi" | "token" | "date"
  initialValue,
  onApply,
  initialStartDate,
  initialEndDate,
}) => {
  const [btnRef, ddStyle] = useDropdownPosition(
    showDropdown,
    320,
    containerRef
  );

  return (
    <div
      key={id}
      className={`applied-filter ${selected?.length > 0 ? "active" : ""}`}
    >
      <button
        className="hubspot-filter-button"
        ref={btnRef}
        onClick={onOpenDropdown}
      >
        {selected?.length > 0 && type === "multi" && (
          <div className="filter-count">{selected.length}</div>
        )}
        <span>{label}</span>
        {chevronDown(selected?.length > 0 ? "#2AA251" : "#3F3F46", 20, 20)}
        {selected?.length > 0 && type === "multi" && (
          <div
            style={{ height: "20px" }}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          >
            {modalCloseIcon("#70707B", 20, 20)}
          </div>
        )}
      </button>

      {showDropdown && type === "multi" && (
        <MultiSelectDropdown
          style={{ ...ddStyle, top: 42, position: "absolute", zIndex: 9999 }}
          options={options}
          onAdd={onAdd}
          onClear={onClear}
          selected={selected}
        />
      )}

      {showDropdown && type === "token" && (
        <ContainsTokenDropdown
          style={{ ...ddStyle, top: 42, position: "absolute", zIndex: 9999 }}
          label={label}
          onApply={onApply}
          onClose={onClear}
          initialEmailVal={initialValue}
        />
      )}

      {showDropdown && type === "date" && (
        <DateFilterDropdown
          style={{ ...ddStyle, top: 42, position: "absolute", zIndex: 9999 }}
          onApply={onApply}
          onCancel={onClear}
          initialStartDate={initialStartDate}
          initialEndDate={initialEndDate}
        />
      )}
    </div>
  );
};
